import { useState, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "motion/react";
import { Mail, Lock, ShieldCheck, ArrowRight, Sparkles, Clock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, ApiError } from "@/services/api";

/**
 * Animated background particles.
 */
function ParticleField() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(40)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-white rounded-full"
          initial={{
            x: Math.random() * (typeof window !== "undefined" ? window.innerWidth : 1400),
            y: Math.random() * (typeof window !== "undefined" ? window.innerHeight : 900),
            opacity: Math.random() * 0.3 + 0.1,
            scale: Math.random() * 0.5 + 0.5,
          }}
          animate={{
            y: [null, Math.random() * (typeof window !== "undefined" ? window.innerHeight : 900)],
            opacity: [null, Math.random() * 0.3 + 0.1, 0],
          }}
          transition={{
            duration: Math.random() * 15 + 10,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}
    </div>
  );
}

/**
 * Animated gradient orbs.
 */
function GradientOrbs() {
  return (
    <>
      <motion.div
        className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-amber-500/20 rounded-full blur-[100px]"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-[#124C77]/40 rounded-full blur-[120px]"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.4, 0.25, 0.4],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </>
  );
}

/**
 * Styled input component for auth forms.
 */
interface AuthInputProps {
  id: string;
  type: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  icon: React.ReactNode;
  disabled?: boolean;
  error?: string;
  minLength?: number;
}

function AuthInput({
  id,
  type,
  placeholder,
  value,
  onChange,
  icon,
  disabled,
  error,
  minLength,
}: AuthInputProps) {
  return (
    <div className="space-y-1">
      <div className="relative group">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-amber-400 transition-colors">
          {icon}
        </div>
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          disabled={disabled}
          minLength={minLength}
          className={`w-full pl-12 pr-4 py-4 bg-white/5 border ${
            error ? "border-red-500/50" : "border-white/10"
          } rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/50 focus:bg-white/10 transition-all duration-300 disabled:opacity-50`}
        />
      </div>
      {error && (
        <p className="text-red-400 text-xs pl-4">{error}</p>
      )}
    </div>
  );
}

/**
 * Register page component with premium design.
 */
export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [waitlistMessage, setWaitlistMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.register(email, password);

      if (response.waitlisted) {
        setWaitlistMessage(response.message || "Your account is pending admin approval.");
        return;
      }

      if (response.access_token && response.user) {
        login(response.access_token, response.user);

        // Fetch workspaces and select the first one
        try {
          const workspacesResponse = await api.listWorkspaces();
          if (workspacesResponse.workspaces.length > 0) {
            const firstWorkspace = workspacesResponse.workspaces[0];
            localStorage.setItem("active_workspace_id", firstWorkspace.id);
          }
        } catch (workspaceErr) {
          console.warn("Failed to fetch workspaces on registration:", workspaceErr);
        }

        navigate("/analysis");
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "Registration failed");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-[#063F6B] via-[#124C77] to-[#042a4d] flex items-center justify-center p-4">
      <ParticleField />
      <GradientOrbs />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/">
            <img
              src="/logo-light.png"
              alt="Feedforward AI"
              className="h-12 w-auto mx-auto mb-6 hover:scale-105 transition-transform"
            />
          </Link>
        </motion.div>

        {/* Card */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {/* Glow effect behind card */}
          <div className="absolute -inset-1 bg-gradient-to-r from-[#124C77]/20 to-[#EAA059]/20 rounded-2xl blur-xl" />
          
          <div className="relative bg-[#063F6B]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
            {waitlistMessage ? (
              /* Waitlist confirmation */
              <motion.div
                className="text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-8 h-8 text-amber-400" />
                </div>
                <h1
                  className="text-3xl text-white mb-3"
                  style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
                >
                  You're on the Waitlist
                </h1>
                <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                  {waitlistMessage}
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-white font-medium transition-all"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  Back to Login
                </Link>
              </motion.div>
            ) : (
              <>
                {/* Header */}
                <div className="text-center mb-8">
                  <motion.div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-xs font-medium text-amber-400">Get Started Free</span>
                  </motion.div>

                  <h1
                    className="text-3xl text-white mb-2"
                    style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
                  >
                    Create Account
                  </h1>
                  <p className="text-slate-400 text-sm">
                    Join us and start evaluating AI vendors today
                  </p>
                  <p className="text-slate-500 text-xs mt-2">
                    If you were invited by a Feedforward member, please create an account using your company email.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  <AuthInput
                    id="email"
                    type="email"
                    placeholder="Email address (@company.com)"
                    value={email}
                    onChange={setEmail}
                    icon={<Mail className="w-5 h-5" />}
                    disabled={isLoading}
                    error={error && error.toLowerCase().includes("email") ? error : undefined}
                  />

                  <AuthInput
                    id="password"
                    type="password"
                    placeholder="Password (min. 8 characters)"
                    value={password}
                    onChange={setPassword}
                    icon={<Lock className="w-5 h-5" />}
                    disabled={isLoading}
                    minLength={8}
                    error={error && error.toLowerCase().includes("password") && !error.toLowerCase().includes("match") ? error : undefined}
                  />

                  <AuthInput
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    icon={<ShieldCheck className="w-5 h-5" />}
                    disabled={isLoading}
                    minLength={8}
                    error={error && error.toLowerCase().includes("match") ? error : undefined}
                  />

                  {error &&
                   !error.toLowerCase().includes("email") &&
                   !error.toLowerCase().includes("password") &&
                   !error.toLowerCase().includes("match") && (
                    <motion.div
                      className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {error}
                    </motion.div>
                  )}

                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    className="group relative w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-xl shadow-lg shadow-amber-500/25 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {isLoading ? (
                        <>
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Creating account...
                        </>
                      ) : (
                        <>
                          Create Account
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </span>
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-700"
                      initial={{ x: "100%" }}
                      whileHover={{ x: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.button>
                </form>

                {/* Footer */}
                <div className="mt-8 text-center">
                  <p className="text-slate-400 text-sm">
                    Already have an account?{" "}
                    <Link
                      to="/login"
                      className="text-amber-400 hover:text-amber-300 font-medium transition-colors"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Back to home link */}
        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Link
            to="/"
            className="text-slate-500 hover:text-white text-sm transition-colors inline-flex items-center gap-1"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to home
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
